import { post, get, getCookie } from './request.js'
import { MessageBox } from 'element-ui'

/**
 * 认证服务类
 */
class AuthService {
  /**
   * 自动登录
   * @returns {Promise<Object>} 登录结果
   */
  async autoLogin() {
    try {
      const xxwebToken = getCookie('xxweb_token');
      if (!xxwebToken) {
        // 未找到cookie，直接弹窗提示并引导去登录
        MessageBox.confirm('未登录，请先去登录', '提示', {
          confirmButtonText: '确认',
          showCancelButton: false,
          type: 'warning',
          closeOnClickModal: false,
          closeOnPressEscape: false
        }).then(() => {
          this.logout();
        }).catch(() => {});
        return { success: false, message: '未找到登录凭证' };
      }

      console.log('开始自动登录...');
      
      // 解码cookie值，避免URL编码问题
      const decodedToken = decodeURIComponent(xxwebToken);
      // console.log('原始cookie值:', xxwebToken);
      // console.log('解码后的值:', decodedToken);
      
      // 创建 FormData 对象，使用 form 表单格式
      const formData = new FormData();
      formData.append('scene', 'cookie');
      formData.append('code', decodedToken);
      
      const response = await post('/api.php/v1.login/login', formData);
      
      if (response.data.code === 0) {
        // 登录成功
        const token = response.data.data.token;
        localStorage.setItem('token', token);
        // 强制刷新页面（使用会话级防抖，避免无限刷新）
        try {
          if (!sessionStorage.getItem('reloaded_after_login')) {
            sessionStorage.setItem('reloaded_after_login', '1');
            setTimeout(() => { window.location.reload(); }, 50);
          }
        } catch (e) {
          // 忽略 sessionStorage 不可用等异常
        }
        return { 
          success: true, 
          token: token, 
          message: '登录成功' 
        };
      } else {
        MessageBox.confirm('未登录，请先去登录', '提示', {
          confirmButtonText: '确认',
          showCancelButton: false,
          type: 'warning',
          closeOnClickModal: false,
          closeOnPressEscape: false
        }).then(() => {
          this.logout();
        }).catch(() => {});
        return { 
          success: false, 
          message: response.data.msg || '登录失败' 
        };
      }
    } catch (error) {
      MessageBox.confirm('未登录，请先去登录', '提示', {
        confirmButtonText: '确认',
        showCancelButton: false,
        type: 'warning',
        closeOnClickModal: false,
        closeOnPressEscape: false
      }).then(() => {
        this.logout();
      }).catch(() => {});
      return { 
        success: false, 
        message: '网络错误，请稍后重试' 
      };
    }
  }

  /**
   * 检查是否已登录
   * @returns {boolean}
   */
  isLoggedIn() {
    return !!localStorage.getItem('token');
  }

  /**
   * 获取当前token
   * @returns {string|null}
   */
  getToken() {
    return localStorage.getItem('token');
  }

  /**
   * 清除登录状态
   */
  async logout() {
    try {
      // 调用退出登录接口
      const response = await fetch('https://www.camh.org.cn/user/clearck', {
        method: 'GET',
        credentials: 'include' // 包含cookies
      });
      
      const result = await response.json();
      console.log('退出登录接口返回:', result);
      
      // 清空 Local Storage 中的所有数据
      localStorage.clear();
      
      // 清空 Cookies 中的所有数据
      const cookies = document.cookie.split(';');
      cookies.forEach(cookie => {
        const eqPos = cookie.indexOf('=');
        const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();
        if (name) {
          // 删除cookie，设置过期时间为过去的时间
          document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
        }
      });
      
      console.log('已清除登录状态、Local Storage 和 Cookies');
      
      // 跳转到登录页面
      window.location.href = 'https://www.camh.org.cn/user/login.html';
    } catch (error) {
      console.error('退出登录时出错:', error);
      // 即使接口调用失败，也要清除本地数据
      localStorage.clear();
      
      // 清空 Cookies
      const cookies = document.cookie.split(';');
      cookies.forEach(cookie => {
        const eqPos = cookie.indexOf('=');
        const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();
        if (name) {
          document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
        }
      });
      
      // 即使出错也要跳转到登录页面
      window.location.href = 'https://www.camh.org.cn/user/login.html';
    }
  }

  /**
   * 获取用户信息
   * @returns {Promise<Object>} 用户信息
   */
  async getUserInfo() {
    try {
      const token = this.getToken();
      if (!token) {
        // 未登录，直接返回，不调用接口（避免在登录前调用）
        console.log('登录未完成，跳过获取用户信息接口调用');
        return { 
          success: false, 
          message: '未登录，请先登录',
          data: null
        };
      }

      // console.log('开始获取用户信息...');
      
      const response = await get('/api.php/v1.user/info');
      
      if (response.data.code === 0) {
        // 获取成功
        const userInfo = response.data.data;
        
        // 检查返回的data是否为空（null、undefined、空对象）
        const isEmpty = 
          userInfo === null || 
          userInfo === undefined || 
          (userInfo && typeof userInfo === 'object' && Object.keys(userInfo).length === 0);
        
        if (isEmpty) {
          console.log('用户信息接口返回空数据，准备刷新页面:', userInfo);
          try {
            // 获取当前刷新次数
            const reloadCount = parseInt(sessionStorage.getItem('reload_count_userinfo') || '0', 10);
            
            // 最多刷新5次
            if (reloadCount < 5) {
              // 增加刷新次数
              sessionStorage.setItem('reload_count_userinfo', String(reloadCount + 1));
              console.log(`开始刷新页面... (第 ${reloadCount + 1} 次)`);
              setTimeout(() => { window.location.reload(); }, 50);
            } else {
              console.log('已达到最大刷新次数(5次)，停止刷新');
              // 清除刷新计数，避免影响后续
              sessionStorage.removeItem('reload_count_userinfo');
            }
          } catch (e) {
            console.error('刷新页面时出错:', e);
            // 忽略 sessionStorage 不可用等异常
          }
        } else {
          // 如果数据不为空，清除刷新计数
          try {
            sessionStorage.removeItem('reload_count_userinfo');
          } catch (e) {
            // 忽略异常
          }
        }
        
        // console.log('获取用户信息成功:', userInfo);
        return { 
          success: true, 
          data: userInfo, 
          message: '获取用户信息成功' 
        };
      } else {
        console.log('获取用户信息失败:', response.data.msg);
        // 获取失败，提示并引导去登录
        MessageBox.confirm('未登录，请先去登录', '提示', {
          confirmButtonText: '确认',
          showCancelButton: false,
          type: 'warning',
          closeOnClickModal: false,
          closeOnPressEscape: false
        }).then(() => {
          this.logout();
        }).catch(() => {});
        return { 
          success: false, 
          message: response.data.msg || '获取用户信息失败' 
        };
      }
    } catch (error) {
      console.error('获取用户信息出错:', error);
      // 异常情况也提示
      MessageBox.confirm('未登录，请先去登录', '提示', {
        confirmButtonText: '确认',
        showCancelButton: false,
        type: 'warning',
        closeOnClickModal: false,
        closeOnPressEscape: false
      }).then(() => {
        this.logout();
      }).catch(() => {});
      return { 
        success: false, 
        message: '网络错误，请稍后重试' 
      };
    }
  }
}

export default new AuthService(); 